import React, { useState, useEffect } from 'react'
import { Table, Typography, Empty } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { formatCurrency } from '../pettCashUtils'
import { fetchDeletedTransactions } from '../../../api/transactionsApiService/transactionsApiUtils'

import Button from '../../../components/common/button/button'
import Modal from '../../../components/common/modal/modal'
import Input from '../../../components/common/input/input'
import Spinner from '../../../components/common/spinner/spinner'

import { useMsal } from '@azure/msal-react'

const { Text } = Typography

const DeletedTransactionsModal = ({ visible, onClose, selectedDate }) => {
  const [deletedTransactions, setDeletedTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const { instance } = useMsal()

  useEffect(() => {
    const loadDeletedTransactions = async () => {
      if (!visible) return

      try {
        setLoading(true)

        // Extract month and year from the selectedDate for API call
        const month = selectedDate.format('MM') // Format as '01', '02', etc.
        const year = selectedDate.format('YYYY')

        // Call API to get deleted transactions
        const response = await fetchDeletedTransactions(instance, { month, year })

        // Process the response - handle both array and single object responses
        if (response) {
          let transactionsArray = []

          // If the response is the exact structure from the provided example
          if (Array.isArray(response)) {
            transactionsArray = response
          }
          // If it's coming from the API with the expected structure
          else if (response.pettyCashTransactions) {
            transactionsArray = Array.isArray(response.pettyCashTransactions)
              ? response.pettyCashTransactions
              : response.pettyCashTransactions.records || [response.pettyCashTransactions]
          }

          if (transactionsArray && transactionsArray.length > 0) {
            // Format the deleted transactions
            const formattedTransactions = transactionsArray.map((item, index) => {
              // Determine transaction type based on cashFlowType field
              const isTransactionIn = item.cashFlowType === 'cash-in'

              // Format date and time values from invoiceDateTime
              const dateTime = item.invoiceDateTime ? new Date(item.invoiceDateTime) : new Date()
              const dateStr = dateTime.toISOString().split('T')[0]
              const timeStr = dateTime.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              })

              // Use appropriate fields for deletion info
              const deletedBy = item.deletedBy || item.updatedBy || item.createdBy || 'System'
              const deletedAt = item.deletedAt || item.updatedAt || item.createdAt || new Date().toISOString()

              const createdBy = item.createdBy || 'System'
              const createdAt = item.transactionCreationDate || new Date().toISOString()

              return {
                key: `deleted-${index + 1}`,
                srNo: index + 1,
                transactionId: item.transactionId || '',
                invoiceNumber: item.invoiceNumber || '',
                date: dateStr,
                time: timeStr,
                description: item.description || '',
                expenseType: item.transactionUsage || '',
                cashOut: !isTransactionIn ? item.amount : null,
                cashIn: isTransactionIn ? item.amount : null,
                deletedBy: deletedBy,
                deletedAt: deletedAt,
                createdBy: createdBy,
                createdAt: createdAt,
                // Additional properties that might be useful
                _id: item._id,
                storeId: item.storeId,
                hasAttachments: item.attachments && item.attachments.length > 0
              }
            })

            setDeletedTransactions(formattedTransactions)
          } else {
            // No deleted transactions found
            setDeletedTransactions([])
          }
        } else {
          // Handle empty response
          setDeletedTransactions([])
        }
      } catch (error) {
        console.error('Error loading deleted transactions:', error)
        setDeletedTransactions([])
      } finally {
        setLoading(false)
      }
    }

    loadDeletedTransactions()
  }, [visible, selectedDate, instance])

  // Get search props for columns
  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters()
              confirm()
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </div>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      return record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : false
    }
  })

  // Format the date
  const formatDate = (dateStr) => {
    if (!dateStr) return ''

    // If it's a ISO date string from the JS Date
    if (dateStr.includes('T')) {
      const date = new Date(dateStr)
      return `${date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })} ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })}`
    }
    return dateStr
  }

  // Define table columns
  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'srNo',
      key: 'srNo',
      width: 80
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      width: 140,
      ...getColumnSearchProps('transactionId', 'Search ID')
    },
    {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 140,
      ...getColumnSearchProps('invoiceNumber', 'Search invoice')
    },
    {
      title: 'Date & Time',
      dataIndex: 'date',
      key: 'date',
      width: 180,
      render: (text, record) => `${record.date} ${record.time}`
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200
    },
    {
      title: 'Expense Type',
      dataIndex: 'expenseType',
      key: 'expenseType',
      width: 140
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 140,
      ...getColumnSearchProps('createdBy', 'Search creator')
    },
    {
      title: 'Created Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: text => formatDate(text)
    },
    {
      title: 'Deleted By',
      dataIndex: 'deletedBy',
      key: 'deletedBy',
      width: 140,
      ...getColumnSearchProps('deletedBy', 'Search user')
    },
    {
      title: 'Deleted At',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 180,
      render: text => formatDate(text)
    },
    {
      title: 'Cash Out',
      dataIndex: 'cashOut',
      key: 'cashOut',
      width: 120,
      align: 'right',
      render: (text) =>
        text && (
          <Text style={{ color: '#d9363e' }}>{formatCurrency(text)}</Text>
        )
    },
    {
      title: 'Cash In',
      dataIndex: 'cashIn',
      key: 'cashIn',
      width: 120,
      align: 'right',
      render: (text) =>
        text && (
          <Text style={{ color: '#52c41a' }}>{formatCurrency(text)}</Text>
        )
    }
  ]

  return (
    <Modal
      title="Deleted Transactions Log"
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      className="deleted-logs-modal"
    >
      <div className="deleted-logs-container">
        {loading ? (
          <div className="spinner-container" style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spinner size="large" />
          </div>
        ) : deletedTransactions.length > 0 ? (
          <Table
            columns={columns}
            dataSource={deletedTransactions}
            pagination={{ pageSize: 5 }}
            scroll={{ x: 'max-content' }}
          />
        ) : (
          <Empty
            description="No deleted transactions found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: '40px 0' }}
          />
        )}
      </div>
    </Modal>
  )
}

export default DeletedTransactionsModal